import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Target, Users, Layers, Volume2, ShieldCheck, Swords, MessageCircle, Hash, Radio, Trophy, Save, Pencil, X, History, RotateCcw } from "lucide-react";

const FIELDS = [
  { key: "brand_objective", label: "Brand Objective", icon: Target, tint: "from-orange-500/30 to-red-500/20 border-orange-500/30 text-orange-700", placeholder: "The North Star: what does this brand exist to do?" },
  { key: "target_audience", label: "Target Audience", icon: Users, tint: "from-amber-500/30 to-orange-500/20 border-amber-500/30 text-amber-700", placeholder: "Age, geography, interests, jobs to be done…" },
  { key: "content_pillars", label: "Content Pillars", icon: Layers, tint: "from-red-500/30 to-rose-500/20 border-red-500/30 text-red-700", placeholder: "3-6 recurring themes (one per line)" },
  { key: "tone_of_voice", label: "Tone of Voice", icon: Volume2, tint: "from-fuchsia-500/30 to-pink-500/20 border-fuchsia-500/30 text-fuchsia-700", placeholder: "Playful, expert, no-jargon, warm…" },
  { key: "brand_guidelines", label: "Brand Guidelines", icon: ShieldCheck, tint: "from-emerald-500/30 to-teal-500/20 border-emerald-500/30 text-emerald-700", placeholder: "Colors, typography, imagery rules, dos and don'ts" },
  { key: "competitors", label: "Competitors", icon: Swords, tint: "from-rose-500/30 to-red-500/20 border-rose-500/30 text-rose-700", placeholder: "Direct + aspirational competitors and what to differentiate" },
  { key: "key_messages", label: "Key Messages", icon: MessageCircle, tint: "from-blue-500/30 to-cyan-500/20 border-blue-500/30 text-blue-700", placeholder: "The 3-5 sticky things we always say" },
  { key: "hashtag_strategy", label: "Hashtag Strategy", icon: Hash, tint: "from-orange-500/30 to-yellow-500/20 border-orange-500/30 text-orange-700", placeholder: "Branded, niche and trending buckets" },
  { key: "platform_strategy", label: "Platform Strategy", icon: Radio, tint: "from-teal-500/30 to-emerald-500/20 border-teal-500/30 text-teal-700", placeholder: "Per-platform priorities, cadence and formats" },
  { key: "monthly_goals", label: "Monthly Goals", icon: Trophy, tint: "from-amber-500/30 to-orange-500/20 border-amber-500/30 text-amber-700", placeholder: "Numbers, launches and campaigns to hit this month" },
];

export default function StrategyPanel({ clientId, clientName, readOnly = false }) {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const canEdit = !readOnly && ["admin","ceo","coo","manager","account_manager"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/strategy`);
      setDoc(data);
      setForm(Object.fromEntries(FIELDS.map(f => [f.key, data[f.key] || ""])));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(()=>{ if (clientId) load(); }, [clientId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${clientId}/strategy`, form);
      toast.success("Strategy saved & team notified");
      setEditing(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!doc) return <div className="p-6 text-neutral-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="panel p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-orange-400">Strategy Doc</div>
          <div className="text-lg font-bold tracking-tight mt-0.5">{clientName || "Client"} · Playbook</div>
          <div className="text-[11px] font-mono text-neutral-500 mt-1">
            {doc.updated_at ? <>Last edited by <span className="text-neutral-700">{doc.updated_by_name || "—"}</span> · {new Date(doc.updated_at).toLocaleString()}</> : "Not published yet"}
          </div>
        </div>
        {canEdit && (
          editing ? (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={()=>{ setEditing(false); setForm(Object.fromEntries(FIELDS.map(f=>[f.key, doc[f.key]||""]))); }} data-testid="strategy-cancel">
                <X className="w-4 h-4 mr-1"/> Cancel
              </Button>
              <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="strategy-save">
                <Save className="w-4 h-4 mr-1"/> {busy ? "Saving..." : "Publish Strategy"}
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={()=>setHistoryOpen(true)} className="border-[#E4E7EC]" data-testid="strategy-history-btn">
                <History className="w-4 h-4 mr-1"/> Version History
              </Button>
              <Button onClick={()=>setEditing(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="strategy-edit">
                <Pencil className="w-4 h-4 mr-1"/> Edit Strategy
              </Button>
            </div>
          )
        )}
        {!canEdit && !readOnly && (
          <Button variant="outline" onClick={()=>setHistoryOpen(true)} className="border-[#E4E7EC]" data-testid="strategy-history-btn">
            <History className="w-4 h-4 mr-1"/> Version History
          </Button>
        )}
      </div>

      <StrategyHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} clientId={clientId} canRestore={canEdit} onRestored={()=>{ setHistoryOpen(false); load(); }}/>

      <div className="grid md:grid-cols-2 gap-4">
        {FIELDS.map(f => {
          const Icon = f.icon;
          const value = editing ? form[f.key] : doc[f.key];
          return (
            <div key={f.key} className="panel p-5" data-testid={`strategy-card-${f.key}`}>
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${f.tint} border grid place-items-center`}>
                  <Icon className="w-4 h-4"/>
                </div>
                <div className="text-sm font-bold tracking-tight">{f.label}</div>
              </div>
              {editing ? (
                <Textarea rows={5} value={form[f.key]} onChange={e=>setForm({...form, [f.key]: e.target.value})} placeholder={f.placeholder} data-testid={`strategy-input-${f.key}`} className="mt-3 bg-[#FFFFFF] border-[#E4E7EC]"/>
              ) : (
                value ? (
                  <div className="mt-3 text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed" data-testid={`strategy-value-${f.key}`}>{value}</div>
                ) : (
                  <div className="mt-3 text-sm text-neutral-500 italic">{f.placeholder}</div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StrategyHistoryDialog({ open, onOpenChange, clientId, canRestore, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (!open) { setSelected(null); setConfirm(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/strategy/versions`);
        setVersions(data);
        setSelected(data[0] || null);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    })();
  }, [open, clientId]);

  const restore = async (v) => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/strategy/versions/${v.id}/restore`);
      toast.success("Strategy restored — an auto-snapshot of the previous version was kept.");
      onRestored?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirm(null); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="strategy-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2"><History className="w-5 h-5 text-orange-500"/> Strategy Version History</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Every edit auto-snapshots the previous version. Restore any one — the current version is kept safely in history.</DialogDescription>
        </DialogHeader>
        {versions.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 text-sm">No previous versions yet. Every future edit will appear here automatically.</div>
        ) : (
          <div className="grid grid-cols-12 gap-4 overflow-hidden flex-1">
            <div className="col-span-4 overflow-y-auto border-r border-[#E4E7EC] pr-3 space-y-1.5">
              {versions.map(v => (
                <button key={v.id} onClick={()=>setSelected(v)} data-testid={`strategy-version-${v.id}`} className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.id===v.id ? "border-orange-500/50 bg-orange-500/10" : "border-[#E4E7EC] hover:border-[#CBD1DC]"}`}>
                  <div className="text-[11px] font-mono text-neutral-600">{fmt(v.snapshotted_at)}</div>
                  <div className="text-sm font-semibold text-neutral-800 mt-0.5">
                    {v.prev_updated_by_name || v.snapshotted_by_name || "—"}
                  </div>
                  {v.restore_of && <div className="text-[10px] font-mono text-orange-700 mt-1">Auto-snap before a restore</div>}
                </button>
              ))}
            </div>
            <div className="col-span-8 overflow-y-auto">
              {selected ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Snapshot from</div>
                      <div className="text-sm font-semibold">{fmt(selected.snapshotted_at)}</div>
                      <div className="text-[11px] text-neutral-600 mt-0.5">Prev editor: {selected.prev_updated_by_name || "—"}</div>
                    </div>
                    {canRestore && (
                      <Button onClick={()=>setConfirm(selected)} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid={`strategy-restore-${selected.id}`}>
                        <RotateCcw className="w-4 h-4 mr-1"/> Restore this version
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {FIELDS.map(f => selected[f.key] ? (
                      <div key={f.key} className="rounded-lg border border-[#E4E7EC] p-3 bg-[#F5F7FB]">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{f.label}</div>
                        <div className="text-sm text-neutral-800 whitespace-pre-wrap mt-1">{selected[f.key]}</div>
                      </div>
                    ) : null)}
                  </div>
                </div>
              ) : <div className="text-neutral-500 text-sm p-6">Pick a version on the left to preview it.</div>}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="strategy-history-close">Close</Button>
        </DialogFooter>
        <Dialog open={!!confirm} onOpenChange={(v)=>!v && setConfirm(null)}>
          <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-md" data-testid="strategy-restore-confirm">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold flex items-center gap-2 text-orange-700"><RotateCcw className="w-5 h-5"/> Restore this version?</DialogTitle>
              <DialogDescription className="text-neutral-600 text-xs">
                The current strategy will be auto-snapshotted first so you can roll back at any time.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={()=>setConfirm(null)} data-testid="strategy-restore-cancel">Cancel</Button>
              <Button onClick={()=>restore(confirm)} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="strategy-restore-confirm-btn">{busy ? "Restoring..." : "Restore"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
